/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable import/order */
import {observable, runInAction} from 'mobx';

import {FooterData} from '@udemy/footer';
import {getClientAppContext} from '@udemy/nextjs-core';
import {HeaderData} from '@udemy/react-header';
import {CommonStore} from '@udemy/store-provider';

// <<Stencil::Block(userImports)>>
import {GwgAccelerator, getGwgAccelerator} from '@udemy/career-academies';
import {CertificationBundle} from '@udemy/react-certification-preparation';
import {UserOccupationInfo} from '@udemy/react-personalized-content';
import {udSentry} from '@udemy/sentry';
import {udApi} from '@udemy/ud-api';
import {Brand} from '@udemy/ud-data';

import type {BadgeClass} from '@udemy/graphql';

import {BILLBOARD_NOTICE_TYPE} from 'src/features/constants';
import {CertificationVoucherExam} from 'src/generated/graphql';
import {fetcher} from 'src/lib/gql-fetcher';
import {HomeContextData} from 'src/types/home-context';
import {BillboardDataResponseResult, UserEnrollmentsData} from 'src/types/page-props';
// <</Stencil::Block>>

export interface RootStoreData {
    footerData: FooterData;
    headerData: HeaderData;
    locale: string;
    // <<Stencil::Block(rootStoreData)>>
    contextData: HomeContextData;
    userOccupationInfo: UserOccupationInfo;
    userEnrollments?: UserEnrollmentsData;
    billboardData: {
        results: BillboardDataResponseResult[];
    };
    brand: Brand;
    currency: string;
    hasSeenUserGoalHeader: boolean;
    accelerator?: GwgAccelerator;
    recommendedExam?: CertificationVoucherExam | null;
    topicSlug?: string | null;
    bundle?: CertificationBundle | null;
    certification?: BadgeClass | null;
    // <</Stencil::Block>>
}

// <<Stencil::Block(userDefined)>>

// <</Stencil::Block>>

/**
 * RootStore
 * This is a store class that acts a convienent place to store global application data that can be accessed via `useStores([RootStore])`.
 */
export class RootStore extends CommonStore {
    static readonly STORE_ID = 'RootStore';

    @observable headerData: HeaderData;
    @observable footerData: FooterData;

    // <<Stencil::Block(classProperties)>>
    @observable contextData: HomeContextData;
    @observable userOccupationInfo: UserOccupationInfo;
    @observable userEnrollments?: UserEnrollmentsData;
    @observable billboardData: {
        results: BillboardDataResponseResult[];
    };
    @observable brand: Brand;
    @observable currency: string;
    @observable hasSeenUserGoalHeader: boolean;
    @observable accelerator?: GwgAccelerator;
    @observable recommendedExam?: CertificationVoucherExam | null;
    @observable topicSlug?: string | null;
    @observable bundle?: CertificationBundle | null;
    @observable certification?: BadgeClass | null;
    // <</Stencil::Block>>

    constructor(private readonly data: RootStoreData) {
        super();

        // Store initialization data
        this.headerData = data.headerData;
        this.footerData = data.footerData;

        // <<Stencil::Block(userConstructor)>>
        this.contextData = data.contextData;
        this.userOccupationInfo = data.userOccupationInfo;
        this.userEnrollments = data.userEnrollments;
        this.billboardData = data.billboardData;
        this.brand = data.brand;
        this.currency = data.currency;
        this.hasSeenUserGoalHeader = data.hasSeenUserGoalHeader;
        this.accelerator = data.accelerator;
        this.recommendedExam = data.recommendedExam;
        this.topicSlug = data.topicSlug;
        this.bundle = data.bundle;
        this.certification = data.certification;

        // Only call in client context if data not passed via server-side props
        if (typeof window !== 'undefined') {
            if (!this.contextData) {
                this.fetchContextData();
            }

            if (!this.billboardData) {
                this.fetchBillboardData();
            }

            if (!this.accelerator) {
                this.fetchAcceleratorData();
            }
        }
        // <</Stencil::Block>>
    }

    // <<Stencil::Block(userMethods)>>
    async fetchContextData() {
        try {
            const params = {
                locale: this.headerData.currentLocale,
            };
            const response = await udApi.get('browse/lihp-context/', {params});

            this.contextData = response.data;
        } catch (error) {
            udSentry.captureException(error);
        }
    }

    async fetchBillboardData() {
        try {
            const searchParams = new URLSearchParams(window.location.search);
            const paramEntries = Object.fromEntries(searchParams.entries());

            const params: Record<string, string> = {
                type: BILLBOARD_NOTICE_TYPE,
                locale: this.headerData.currentLocale,
                currency: this.currency ?? 'usd',
                ...paramEntries,
            };

            const url = `notices/me/`;
            const response = await udApi.get(url, {params});

            this.billboardData = response.data;
        } catch (error) {
            udSentry.captureException(error);
        }
    }

    async fetchAcceleratorData() {
        try {
            const locale = this.headerData.currentLocale;
            // Extract language code from locale (e.g., "en_US" -> "en")
            const languageCode = locale.split('_')[0];
            const acceleratorData = await (
                getGwgAccelerator as (
                    path: string,
                    locale: string,
                    options?: {customGqlFetcher?: typeof fetcher},
                ) => ReturnType<typeof getGwgAccelerator>
            )('ai-professional-certificate-learning-path', languageCode, {
                customGqlFetcher: fetcher,
            });

            runInAction(() => {
                this.accelerator = acceleratorData;
            });
        } catch (error) {
            udSentry.captureException(error);
        }
    }
    // <</Stencil::Block>>
}
